<template>
    <div>
        <div class="normal-container relative">
            <div class="pt-3 lg:pt-12">
                <div class="mb-[44px] lg:mb-8">
                    <ShopwareBreadcrumbs :path="breadCrumbs" :retain-filters="keepFilters" />
                </div>
                <div
                    class="mb-5 flex flex-col gap-2 text-center lg:mt-12 lg:mb-[60px] lg:flex-row lg:items-center lg:gap-4"
                    :class="{ 'lg:mb-2!': topContent }">
                    <h1 class="leading-none">
                        {{ pageHeading }}
                    </h1>
                    <p v-if="stableTotal > 0" class="hidden text-xs leading-none lg:block">{{ stableTotal }} artikelen</p>
                </div>
            </div>

            <!-- Top content: brand/brand-category pages via nd/frontend/url seo.topContent, category pages via custom field -->
            <BlockProductListingSeo v-if="topContent" :content="topContent" compact />

            <!-- Sticky bar: mobile categories + toggle filters + active pills + sort -->
            <div
                class="pointer-events-none sticky top-2 z-20 flex min-h-[68px] flex-col justify-between lg:top-6 lg:flex-row">
                <BlockProductListingCategoryQuickLinks
                    class="pointer-events-auto"
                    :categories="filteredChildCategories"
                    :search="manufacturer" />
                <div class="mb-2 flex w-full lg:mb-4 lg:w-auto lg:flex-wrap lg:items-center">
                    <button
                        @click="handleFilterToggle"
                        class="group border-green-550 pointer-events-auto mb-0 flex h-[52px] w-full shrink-0 items-center justify-center rounded-full border-2 bg-white py-2 !text-base duration-300 hover:border-black hover:bg-black hover:text-white lg:mb-2 lg:inline-flex lg:w-max lg:px-3">
                        <AdjustmentsHorizontalIcon class="mr-2 size-5 stroke-2" />
                        <span
                            class="lg:hidden"
                            v-html="$snippets['headless.plp.openFiltersMobile'] || 'Filters tonen'"></span>
                        <span class="hidden lg:inline">
                            <span
                                v-if="filtersVisible"
                                v-html="$snippets['headless.plp.closeFilters'] || 'Filters verbergen'"></span>
                            <span v-else v-html="$snippets['headless.plp.openFilters'] || 'Filters tonen'"></span>
                        </span>
                        <span class="ml-1" :class="{ 'lg:hidden': filtersVisible }">({{ stableTotal }})</span>
                    </button>
                    <BlockProductListingFiltersCurrentFilters
                        v-if="keepFilters"
                        class="pointer-events-auto hidden lg:block"
                        :filters="stableFilters" />
                    <!-- Active filter pills (desktop only) -->
                    <div v-else class="hidden flex-wrap lg:flex">
                        <button
                            v-for="[id, name] in activeFilterNames"
                            :key="'filter-' + id"
                            @click="removePropertyFilter(id)"
                            class="plp-active-filter mb-2 ml-4">
                            {{ name }}
                            <XMarkIcon class="size-4 shrink-0 stroke-2" />
                        </button>
                        <button
                            v-if="route.query['min-price'] && route.query['max-price']"
                            @click="clearPriceFilter"
                            class="plp-active-filter mb-2 ml-4">
                            <span class="currency-symbol">&euro;</span>{{ route.query['min-price'] }} -
                            <span class="currency-symbol">&euro;</span>{{ route.query['max-price'] }}
                            <XMarkIcon class="size-4 shrink-0 stroke-2" />
                        </button>
                        <button
                            v-if="activePropertyIds.length > 0"
                            @click="clearAllFilters"
                            class="plp-clear-filters mb-2 ml-4">
                            {{ $snippets['headless.plp.removeFilters'] || 'Filters wissen' }}
                        </button>
                    </div>
                </div>
                <BlockProductListingSorting
                    class="pointer-events-auto hidden lg:block"
                    :limit="getLimit"
                    :total="stableTotal"
                    :sorts="stableSorts as any"
                    :currentSort="effectiveCurrentSort" />
            </div>

            <BlockProductListingFiltersCurrentFilters v-if="keepFilters" class="lg:hidden" :filters="stableFilters" />

            <div
                class="flex w-full flex-col pt-3 pb-[35px] lg:flex-row lg:pb-12"
                :class="{ 'boxed:gap-[50px] lg:gap-8': filtersVisible }">
                <div
                    class="plp-filters-col shrink-0 overflow-hidden duration-300 ease-in-out max-lg:!max-w-0 max-lg:!min-w-0 lg:transition-[max-width,min-width]"
                    :style="
                        filtersVisible
                            ? { maxWidth: 'var(--plp-filters-w)', minWidth: 'var(--plp-filters-w)' }
                            : { maxWidth: '0px', minWidth: '0px' }
                    ">
                    <BlockProductListingFilters
                        :loading="loading"
                        :total="stableTotal"
                        :sorts="stableSorts as any"
                        :currentSort="effectiveCurrentSort"
                        :search="search"
                        :setFilters="setCurrentFilters"
                        :currentFilters="stableCurrentFilters"
                        :filters="stableFilters"
                        :siblings-loading="siblingsLoading"
                        :category-siblings="displayCategorySiblings"
                        :current-category-id="props.category?.id"
                        :search-category-tree="isSearchPage && !pendingAutoSelect ? stableSearchCategoryTree : null"
                        :pending-auto-select="pendingAutoSelect"
                        :is-manufacturer-page="!!manufacturer"
                        :manufacturer-category-tree="
                            !!manufacturer && !pendingAutoSelect ? stableManufacturerCategoryTree : null
                        "
                        :manufacturer-seo-url-map="manufacturerSeoUrlMap"
                        :tree-settled="hasSettledOnce" />
                </div>
                <div class="w-full">
                    <!-- Load previous pages button -->
                    <div v-if="startPage > 1 && !loading" class="mb-5">
                        <button @click="loadPreviousProducts" class="btn btn-primary mx-auto mb-5">
                            {{ $snippets['headless.plp.previousProducts'] || 'Vorige producten laden' }}
                        </button>
                        <div class="flex items-center">
                            <div class="border-green-550 flex-1 border-b"></div>
                            <p class="mx-[38px]">{{ $snippets['headless.plp.page'] || 'Pagina' }} {{ startPage }}</p>
                            <div class="border-green-550 flex-1 border-b"></div>
                        </div>
                    </div>

                    <div ref="productsContainer">
                        <BlockProductListingProducts
                            v-if="!showNoProductsNotice"
                            :loading="pendingAutoSelect || (loading && allProducts.length === 0)"
                            :products="allProducts" />
                    </div>

                    <!-- No products notice -->
                    <div v-if="showNoProductsNotice" class="flex flex-col items-center py-12 text-center lg:py-20">
                        <p class="!text-base">
                            {{ noProductsText }}
                        </p>
                        <p v-if="keepFilters && hasActiveFilters" class="mt-2 text-sm">
                            Pas je filters aan of wis de selectie om meer artikelen te bekijken.
                        </p>
                        <button v-if="keepFilters && hasActiveFilters" type="button" class="btn mt-4" @click="clearAllFilters">
                            Alle filters wissen
                        </button>
                    </div>

                    <!-- Loading spinner for load more -->
                    <div
                        v-if="loading && allProducts.length > 0"
                        class="mt-6 flex w-full justify-center"
                        role="status"
                        aria-live="polite">
                        <span class="loader"></span>
                    </div>

                    <!-- X van Y bekeken indicator -->
                    <div v-if="!loading && stableTotal > 0" class="flex items-center">
                        <div class="border-green-550 flex-1 border-b"></div>
                        <p class="mx-[38px] !text-base leading-none">
                            {{ allProducts.length }}
                            {{ $snippets['headless.plp.from'] || 'van' }} {{ stableTotal }}
                            {{ $snippets['headless.plp.seen'] || 'bekeken' }}
                        </p>
                        <div class="border-green-550 flex-1 border-b"></div>
                    </div>

                    <!-- Load more button -->
                    <button
                        v-if="!loading && endPage < stableTotalPages && allProducts.length < stableTotal"
                        @click="loadMoreProducts"
                        class="btn btn-primary mx-auto mt-[35px]">
                        {{ $snippets['headless.plp.loadMoreProducts'] || 'Meer producten laden' }}
                    </button>

                    <div class="flex flex-col-reverse gap-8 lg:flex-col">
                        <!-- <UiHeaderUSPs /> -->
                        <BlockProductListingSeo
                            :title="category?.translated?.customFields?.custom_extra_category_information_title"
                            :content="category?.translated?.customFields?.custom_extra_category_information_text" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { AdjustmentsHorizontalIcon, XMarkIcon } from '@heroicons/vue/24/outline';
    import { overwriteLatestPageHistory } from '~/composables/usePageHistoryStorage';
    import { manufacturerAutoSelectTarget } from '~/utils/plpAutoSelect';
    import {
        queryStr,
        isSpecialCategoryItem,
        renderedTreeItems,
        renderedSearchTreeItems,
        deepestSelectedChildren,
        detectActiveVariant,
        resolveManufacturerSeoUrl as resolveSeoUrlFromMap,
    } from '~/utils/plp';

    interface Category {
        id: string;
        parentId?: string;
        active?: boolean;
        translated: {
            name: string;
            customFields?: {
                custom_extra_category_information_title?: string;
                custom_extra_category_information_text?: string;
                custom_extra_category_information_top_text?: string;
            };
        };
        seoUrls: Array<{ seoPathInfo: string }>;
        customFields?: {
            nd_tweakwise_is_brand?: boolean;
            nd_tweakwise_manufacturer_id?: string;
        };
        extensions?: {
            brandCategories?: {
                manufacturerId: string;
                manufacturerName?: string;
                total: number;
                seoUrls: Array<{ categoryId?: string; tweakwiseId?: string; variant?: string; seoPath: string }>;
            };
        };
    }

    const { trackProductList, trackViewSearchResults } = useGTMEcommerce();
    const route = useRoute();
    const keepFilters = useKeepFilters();
    const router = useRouter();
    const manufacturer = useState<string | undefined>(`manufacturer-${route.path}`).value;
    const manufacturerId = inject<string | undefined>('manufacturerId', undefined);
    const manufacturerName = useState<string | undefined>(`manufacturerName-${route.path}`).value;
    const pageTopContent = useState<string | undefined>(`topContent-${route.path}`).value;
    const breadCrumbs = inject('breadcrumbs', []) as any[];
    const mobileFiltersMenuStore = useMobileFiltersMenuStore();

    const props = defineProps<{
        category?: Category;
        overrideTitle?: string;
        sortBy?: string;
    }>();

    // Brand (category) pages deliver top content via the resolved page state,
    // plain category pages via the category custom field
    const topContent = computed(
        () => pageTopContent || props.category?.translated?.customFields?.custom_extra_category_information_top_text,
    );

    const filtersVisible = ref(true);
    provide('filtersVisible', filtersVisible);

    const handleFilterToggle = () => {
        if (window.innerWidth < 1024) {
            mobileFiltersMenuStore.toggleMenu();
        } else {
            filtersVisible.value = !filtersVisible.value;
        }
    };

    const {
        allProducts,
        startPage,
        endPage,
        productsContainer,
        stableTotal,
        stableTotalPages,
        stableFilters,
        stableCurrentFilters,
        stableSorts,
        effectiveCurrentSort,
        isSearchPage,
        loading,
        search,
        getLimit,
        getElements,
        setCurrentFilters,
        getCurrentListing,
        loadMoreProducts,
        loadPreviousProducts,
        stableTweakwiseCategoryNames,
        stableTweakwiseSelectedSubtreeNames,
        stableSearchCategoryTree,
        stableManufacturerCategoryTree,
        lastSettledFullPath,
        hasSettledOnce,
        initialSearchPromise,
    } = usePlpSearch({
        category: props.category,
        sortBy: props.sortBy,
        manufacturer,
        onSearchComplete: elements => {
            const tnQ = queryStr(route.query.tn_q);
            const keyword = queryStr(route.query.search);
            const searchTerm = tnQ || keyword;
            const page = parseInt(queryStr(route.query.p), 10) || 1;

            if (searchTerm && page === 1) {
                const searchItems = elements.filter((p: any) => p?.coverId !== null);
                trackViewSearchResults(searchTerm, searchItems as any);
            }

            if (props.category) {
                const items = elements.filter((p: any) => p?.coverId !== null);
                trackProductList(items as any, String(props.category.id), props.category.translated?.name || '');
            }
        },
    });

    const navigationSource = useState<any>('plp-navigation-source', () => null);
    const listingPath = route.path;
    watch(
        [stableSearchCategoryTree, stableManufacturerCategoryTree, getCurrentListing],
        () => {
            const tree = stableSearchCategoryTree.value || stableManufacturerCategoryTree.value;
            const selectedDepartment = tree?.items?.find(
                (item: any) => item.isSelected && ['dames', 'heren'].includes(item.title?.toLowerCase()),
            );
            navigationSource.value = {
                ...navigationSource.value,
                path: listingPath,
                manufacturer,
                categoryId: props.category?.id,
                categoryPath: (getCurrentListing.value as any)?.extensions?.tweakwise?.categoryPath,
                department: selectedDepartment?.title?.toLowerCase(),
            };
        },
        { immediate: true },
    );

    provide('tweakwiseCategoryNames', stableTweakwiseCategoryNames);
    provide('tweakwiseSelectedSubtreeNames', stableTweakwiseSelectedSubtreeNames);

    const pageHeading = computed(() => props.overrideTitle || props.category?.translated?.name || manufacturerName);

    useCollectionPageSchema({
        enabled: !isSearchPage.value,
        headline: pageHeading,
        products: allProducts,
        breadcrumbs: breadCrumbs,
        categoryName: () => props.category?.translated?.name,
        brandName: manufacturerName,
        startPosition: () => (startPage.value - 1) * (unref(getLimit) || 0) + 1,
    });

    const { hasActiveFilters, clearAllFilters, activePropertyIds, activeFilterNames, removePropertyFilter, clearPriceFilter } = usePlpFilters(stableFilters);
    useHead(() => ({
        meta:
            keepFilters.value && (hasActiveFilters.value || isSearchPage.value)
                ? [{ name: 'robots', content: 'noindex, follow', key: 'robots' }]
                : [],
    }));

    const brandCategories = props.category?.extensions?.brandCategories;
    const brandManufacturerId =
        brandCategories?.manufacturerId ?? props.category?.customFields?.nd_tweakwise_manufacturer_id;

    const { siblingsResult, displayCategorySiblings, siblingsLoading, childCategories } = useCategorySiblingsDisplay({
        category: props.category,
        isSearchPage,
        getCurrentListing,
    });

    // Build categoryId → seoPath map for manufacturer pages (used by SearchCategoryTree for SEO URL navigation)
    const effectiveManufacturerId = brandManufacturerId ?? manufacturer;
    const { apiClient } = useShopwareContext();
    const manufacturerSeoUrlResult = effectiveManufacturerId
        ? useAsyncData(
              `manufacturer-seo-urls-${effectiveManufacturerId}`,
              async () => {
                  // Use preloaded SEO URLs from brandCategories when available
                  if (brandCategories?.seoUrls?.length) {
                      return brandCategories.seoUrls;
                  }
                  // Otherwise fetch from the API
                  try {
                      const { data } = await apiClient.invoke(
                          `ManufacturerSeoUrls get /manufacturer/${effectiveManufacturerId}/seo-urls` as any,
                      );
                      return (data as any)?.seoUrls ?? [];
                  } catch {
                      return [];
                  }
              },
              {
                  getCachedData(key: string, nuxtApp: any) {
                      return nuxtApp.payload.data[key];
                  },
              },
          )
        : { data: ref([]) };

    const manufacturerSeoUrlMap = computed<Record<string, string>>(() => {
        const seoUrls = (manufacturerSeoUrlResult as any).data?.value ?? [];
        const map: Record<string, string> = {};
        // Non-variant (regular) URLs take priority
        const categoryIdByPath: Record<string, string> = {};
        for (const url of seoUrls) {
            if (!url.seoPath || url.variant) continue;
            if (url.categoryId) {
                map[url.categoryId] = url.seoPath;
                categoryIdByPath[url.seoPath] = url.categoryId;
            }
        }
        // Variant URLs (sale/nieuw) as fallback
        for (const url of seoUrls) {
            if (!url.seoPath || !url.variant) continue;

            if (!url.categoryId) {
                map[`variant:${url.variant}`] = url.seoPath;
                continue;
            }
            if (!map[url.categoryId]) {
                map[url.categoryId] = url.seoPath;
            }

            const basePath = url.seoPath.replace(`/${url.variant}/`, '/');
            const mainCategoryId = categoryIdByPath[basePath];
            if (mainCategoryId) {
                map[`${url.variant}:${mainCategoryId}`] = url.seoPath;
            }
        }
        return map;
    });

    watch(
        () => (manufacturerSeoUrlResult as any).data?.value,
        () => {
            const urls = (manufacturerSeoUrlResult as any).data?.value || [];
            const routes: Record<string, any> = {};
            const normalize = (url: string) => '/' + url.replace(/^\//, '').replace(/\/$/, '');
            for (const url of urls) {
                if (url.seoPath)
                    routes[normalize(url.seoPath)] = {
                        categoryId: url.categoryId,
                        search: String(effectiveManufacturerId || ''),
                    };
            }
            const crumbs = unref(breadCrumbs) || [];
            const brandRoot = crumbs.find(
                (crumb: any) => crumb.name?.toLowerCase() === manufacturerName?.toLowerCase() && crumb.url,
            );
            if (brandRoot && effectiveManufacturerId)
                routes[normalize(brandRoot.url)] = { search: String(effectiveManufacturerId) };
            navigationSource.value = { ...navigationSource.value, brandRoutes: routes };
        },
        { immediate: true },
    );

    // Declared above singleCategoryItem/autoSelectTarget on purpose: `watch` evaluates its
    // getter at registration, and that chain reaches resolveManufacturerSeoUrl — reading a
    // `const` declared further down would throw a temporal-dead-zone ReferenceError.
    const activeVariant = computed<'sale' | 'nieuw' | undefined>(() =>
        detectActiveVariant(route.path, queryStr(route.query.variant), stableManufacturerCategoryTree.value?.items),
    );

    const singleCategoryItem = computed<any | undefined>(() => {
        let item: any | undefined;
        if (isSearchPage.value) {
            if (lastSettledFullPath.value !== route.fullPath) return undefined;
            const items = renderedSearchTreeItems(stableSearchCategoryTree.value?.items);
            item = items?.length === 1 ? items[0] : undefined;
        } else if (manufacturer) {
            // Only trust the tree once it was searched for the current URL (avoids drilling on a stale tree).
            if (lastSettledFullPath.value !== route.fullPath) return undefined;
            // Sale/Nieuw count as choices, so only drill when the rendered tree shows a single item.
            const items = renderedTreeItems(stableManufacturerCategoryTree.value?.items);
            item = items.length === 1 ? items[0] : undefined;
            if (item && isSpecialCategoryItem(item)) return undefined;
        }
        return item && !item.isSelected ? item : undefined;
    });

    // Where auto-select navigates, or undefined when there's nothing to do.
    // Use the same destinations as manual selection: prefer the brand SEO URL,
    // otherwise select the full category path on the current brand page.
    const autoSelectTarget = computed<any | undefined>(() => {
        const item = singleCategoryItem.value;
        if (!item) return undefined;
        if (manufacturer) {
            return manufacturerAutoSelectTarget(item, resolveManufacturerSeoUrl(item), route, keepFilters.value);
        }
        if (!item.categoryPath || item.categoryPath === queryStr(route.query.tn_cid)) return undefined;
        return { path: route.path, query: { ...route.query, tn_cid: item.categoryPath, p: '1' } };
    });

    // True while a category is about to be auto-selected — products show as loading and
    // the category tree is hidden until the narrowed page loads.
    const autoSelectNavigation = useCategoryNavigation(
        computed(() => isSearchPage.value ? autoSelectTarget.value : undefined),
        computed(() => isSearchPage.value ? ({
            categoryId: singleCategoryItem.value?.categoryId,
            categoryPath: singleCategoryItem.value?.categoryPath,
            search: queryStr(route.query.tn_q) || queryStr(route.query.search),
        }) : {}),
    );
    const readyAutoSelectTarget = computed(() => isSearchPage.value
        ? autoSelectNavigation.disabled.value ? undefined : autoSelectNavigation.to.value
        : autoSelectTarget.value);
    const pendingAutoSelect = computed(() => !!readyAutoSelectTarget.value ||
        (!!autoSelectTarget.value && isSearchPage.value && autoSelectNavigation.pending.value));

    const showNoProductsNotice = computed(
        () => (keepFilters.value ? hasSettledOnce.value : !!manufacturer) && !loading.value && !pendingAutoSelect.value && allProducts.value.length === 0,
    );

    const { snippets } = useSnippetsStore();
    const noProductsText = computed(() => (snippets as any)?.['headless.plp.noProducts'] || 'Geen producten gevonden');

    // Auto-select the single category (moved from SearchCategoryTree so it runs even when the component is hidden)
    // replace, not push: the drilled-into category takes the place of the page that redirected,
    // so Back returns to where the visitor came from instead of landing on the redirecting page
    // and being bounced forward again.
    // Never fire during setup/hydration — a router call issued there is swallowed, which strands
    // the visitor on a page that hides its tree and shows a skeleton because pendingAutoSelect
    // is true. Hence onMounted for the initial run instead of an `immediate` watcher.
    const runAutoSelect = (target: any) => {
        if (!target || !import.meta.client) return;
        router.replace(target);
    };
    watch(readyAutoSelectTarget, runAutoSelect);
    onMounted(() => nextTick(() => runAutoSelect(readyAutoSelectTarget.value)));

    const { getUrlById: getQuickLinkSeoUrl } = useCategorySeoUrl();

    function resolveManufacturerSeoUrl(item: any): string | undefined {
        return resolveSeoUrlFromMap(item, {
            seoMap: manufacturerSeoUrlMap.value,
            variant: activeVariant.value,
            treeItems: stableManufacturerCategoryTree.value?.items,
        });
    }

    const filteredChildCategories = computed(() => {
        // On manufacturer pages, derive quick-link categories from the Tweakwise tree + SEO URL map
        if (manufacturer && stableManufacturerCategoryTree.value?.items?.length) {
            const treeItems = stableManufacturerCategoryTree.value.items;
            const source = deepestSelectedChildren(treeItems) ?? treeItems;
            return source
                .filter((item: any) => !item.isSelected && !isSpecialCategoryItem(item))
                .map((item: any) => {
                    const seoUrl = resolveManufacturerSeoUrl(item);
                    return {
                        id: item.categoryId,
                        categoryPath: item.categoryPath,
                        translated: { name: item.title },
                        customFields: item.customFields,
                        // Prefer the SEO URL; fall back to the Tweakwise tn_cid so
                        // categories without a SEO route (e.g. Dames/Heren) still show
                        // in the slider and navigate (same as the sidebar tree does).
                        to: seoUrl
                            ? '/' + seoUrl.replace(/^\//, '')
                            : item.categoryPath
                              ? { path: route.path, query: { ...route.query, tn_cid: item.categoryPath, p: '1' } }
                              : undefined,
                    };
                })
                .filter((c: any) => c.to);
        }

        let categories = childCategories.value;

        // Show siblings as quick links on top-level pages:
        // - Shop/Dames/Heren (matched by category ID)
        const currentPath = route.path.replace(/^\/|\/$/g, '');
        const isTopLevelPage = displayCategorySiblings.value.some(
            s => s.id === props.category?.id || s.seoUrls?.[0]?.seoPathInfo?.replace(/^\/|\/$/g, '') === currentPath,
        );
        if (categories.length === 0 && isTopLevelPage && displayCategorySiblings.value.length > 0) {
            categories = displayCategorySiblings.value
                .filter(
                    s =>
                        s.id !== props.category?.id &&
                        s.seoUrls?.[0]?.seoPathInfo?.replace(/^\/|\/$/g, '') !== currentPath,
                )
                .map(c => ({
                    ...c,
                    resolvedUrl: c.seoUrls?.[0]?.seoPathInfo || getQuickLinkSeoUrl(c.id)?.replace(/^\//, '') || null,
                }))
                .filter(c => c.resolvedUrl);
        }

        if (!keepFilters.value && stableTweakwiseCategoryNames.value) {
            return categories.filter(c => stableTweakwiseCategoryNames.value!.has(c.translated.name.toLowerCase()));
        }
        return categories;
    });

    if (import.meta.server) {
        await siblingsResult;
        if (effectiveManufacturerId) {
            await (manufacturerSeoUrlResult as any);
        }
        // Awaiting the initial tweakwise/Shopware search lets the product grid
        // ship in the SSR HTML instead of the skeleton, eliminating a multi-
        // thousand-pixel CLS once products replace the placeholder grid.
        if (initialSearchPromise.value) {
            await initialSearchPromise.value;
        }
    }

    onBeforeMount(() => {
        if (isSearchPage.value) {
            return;
        }

        overwriteLatestPageHistory({
            key: route.path,
            url: route.fullPath,
            type: 'productListingPage',
            priority: 1,
        });
    });
</script>

<style scoped>
    /* Narrower filter sidebar on tablet so the product grid gets more room; full width from desktop (boxed) up. */
    .plp-filters-col {
        --plp-filters-w: 280px;
    }
    @media (min-width: 1440px) {
        .plp-filters-col {
            --plp-filters-w: 347px;
        }
    }

    .loader {
        width: 40px;
        height: 40px;
        border: 5px solid #000;
        border-bottom-color: transparent;
        border-radius: 50%;
        display: inline-block;
        box-sizing: border-box;
        animation: rotation 1s linear infinite;
    }

    @keyframes rotation {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
</style>
